import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Loading from '../Loading';
import CustomTooltip from '../Tooltip';
import Style from '../DataComponent/DataComponent__TableStyle';
import config from '../../config';
import { I18nContext } from 'utils/I18n/context';

function Report({
                    data = [],
                    tableHtml = '',
                    headerName = null,
                    metrics = null,
                    isTableFirst = false,
                    isTableLast = false,
                    isSorting = false,
                    keysMetric = [],
                    sort: sortProp = null,
                    order: orderProp = 'desc',
                    onAction,
                    onSort,
                    tableWrapperClass = {},
                    loading = false,
                }) {
    const i18n = useContext(I18nContext);
    const staticNamesTable = (i18n && (i18n.t?.dataTable || i18n.I18n?.dataTable)) || { noData: 'No data' };

    const [sort, setSort] = useState(sortProp);
    const [order, setOrder] = useState(orderProp);

    useEffect(() => {
        setSort(sortProp);
    }, [sortProp]);

    useEffect(() => {
        setOrder(orderProp);
    }, [orderProp]);

    const wrapperClass = useMemo(
        () => ({ 'table-responsive': true, ...(tableWrapperClass || {}) }),
        [tableWrapperClass]
    );

    const bodyRef = useRef(null);
    useEffect(() => {
        if (bodyRef.current) bodyRef.current.innerHTML = tableHtml || '';
    }, [tableHtml]);

    const handleSort = useCallback(
        (key) => {
            const nextOrder = sort === key ? (order === 'desc' ? 'asc' : 'desc') : 'desc';
            setSort(key);
            setOrder(nextOrder);
            const payload = { sort: key, order: nextOrder, page: config.initialPage };
            if (onSort) onSort(payload);
            else if (onAction) onAction(payload);
        },
        [sort, order, onAction, onSort]
    );

    const renderHeader = () => {
        if (!headerName) return null;
        return (
            <thead className="bold">
            {headerName.map((items, i) => (
                <tr key={i}>
                    {items.map((item, j) =>
                            item.visible ? (
                                <th
                                    className="report-table__th"
                                    key={j}
                                    colSpan={item.colspan}
                                    rowSpan={item.rowspan}
                                >
                                    <span className="report-table__th-content">
                                        {item.description ? (
                                            <CustomTooltip id={`header-${i}-${j}`} tooltip={item.description}>
                                                <span className="report-table__th-text">{item.title}</span>
                                            </CustomTooltip>
                                        ) : (
                                            <span className="report-table__th-text">{item.title}</span>
                                        )}
                                        {item.isSortable && isSorting && item?.config?.key && keysMetric.includes(item.config.key) ? (
                                            <span
                                                className={classNames('report-table__th-sort', { active: sort === item.config.key })}
                                                data-key={item.config.key}
                                                onClick={() => handleSort(item.config.key)}
                                            >
                        <i className={`fa fa-sort-amount-${order}`} />
                      </span>
                                        ) : null}
                                    </span>
                                </th>
                            ) : null
                    )}
                </tr>
            ))}
            </thead>
        );
    };

    return (
        <div className="loading-wrapper">
            {loading && data.length ? (
                <div className="loading-wrapper__item">
                    <Loading />
                </div>
            ) : null}
            <div className={classNames(wrapperClass)}>
                <Style binaryData={metrics} classTemplate={'.report-table tr td:nth-child({{ index }})'} />
                <table
                    className={classNames({
                        table: true,
                        'table-striped': false,
                        'table-bordered': false,
                        'report-table': true,
                        'green-hover': true,
                        'report-table_first': isTableFirst,
                        'report-table_last': isTableLast,
                    })}
                >
                    {renderHeader()}
                    <tbody ref={bodyRef} />
                </table>
                {!data.length ? (loading ? <Loading center /> : <div className="text-center table-notfound-sign">{staticNamesTable.noData}</div>) : null}
            </div>
        </div>
    );
}

Report.propTypes = {
    data: PropTypes.array,
    tableHtml: PropTypes.string,
    headerName: PropTypes.array,
    metrics: PropTypes.any,
    isTableFirst: PropTypes.bool,
    isTableLast: PropTypes.bool,
    isSorting: PropTypes.bool,
    keysMetric: PropTypes.arrayOf(PropTypes.string),
    sort: PropTypes.string,
    order: PropTypes.oneOf(['asc', 'desc']),
    onAction: PropTypes.func,
    onSort: PropTypes.func,
    tableWrapperClass: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    loading: PropTypes.bool,
};

export default Report;
